import {approvalOffered,approvalBlockerMessage} from './workflowApprovalContract';
import {qualityWaitOf} from './workflowQualityWaitPolicy';
// 261010 UI1a + UI2 — nút chính / nút đầu bước / dòng tóm tắt / hộp xác nhận / lỗi L1–L6 của khung 8 bước (THUẦN: không import antd / router — jest CRA không nạp được).
// Mọi quyết định "bấm gì, gọi gì, chữ gì" nằm ở đây; component chỉ vẽ. KHÔNG giá. Nút chính KHÔNG gọi cổng cũ `script/approve` / `workflow/run`.
// UI2: bước 3–8 chạy cùng cơ chế với Tài sản (`stepCapabilities` / `stepRuns` / `gates.steps` theo từng bước). MỘT nguồn điều kiện (`runOfferOf` / `approveBits`) cho nút chính VÀ nút đầu bước.
// Nguồn "đã xong" cho admission: durable gate, độc lập canApprove/approvalPolicy hiện tại (#27 chỉ chặn APPROVE).
// SCRIPT dùng approval.matches; ASSETS và bước 3–8 supported dùng gates.steps[k].verdict === 'OK'; BE cũ giữ fallback theo trạng thái.
import type { WorkflowStatus, WorkflowStepKey, StepRunObject, StepRunView } from '../../types/filmWorkflow';
import { BOARD_STEP_KEYS, STATE_META, boardStepKeyOf, isDoneState, mergedStateOf, mergedSummaryOf, planProgress, stepNameOf, type StateTone } from './workflowBoardUtils';
import { engineChipOf } from './workflowHeroUtils';
import { workflowActionError, planWriteKindsOf, planWriteLabelOf, type ActionError } from './workflowActionPolicy';
import { runSummaryText } from './workflowRunPolicy';
import { serverMessageOf } from './apiErrorText';
import { eventFlagInfosOf, flagInfoOf, type FlagKind } from './workflowFlagUtils';

export interface StepBadge { text: string; tone: StateTone }
export type PrimaryKey = 'UNAVAILABLE' | 'STOP' | 'RUN_SCRIPT' | 'APPROVE_SCRIPT' | 'OPEN_FAILED' | 'RUN_MISSING' | 'APPROVE_ASSETS' | 'APPROVE_STEP' | 'NEXT_DISABLED' | 'DOWNLOAD_CAPCUT' | 'NONE';
export type ConfirmKind = 'APPROVE_SCRIPT' | 'RUN_STAGE1' | 'RERUN_STAGE1' | 'RUN_MISSING' | 'RUN_FAILED' | 'APPROVE_ASSETS' | 'APPROVE_STEP' | 'RUN_STEP_MISSING' | 'RUN_STEP_FAILED';
export type RunStepKey = 'ASSETS' | 'DIRECTING' | 'CINEMATOGRAPHY' | 'MAP' | 'STORYBOARD' | 'PROMPT' | 'VIDEO';
export type ApproveStepKey = 'SCRIPT' | RunStepKey;
export interface ConfirmCtx { step: WorkflowStepKey; next?: WorkflowStepKey }
/** Ngữ cảnh dựng câu hộp xác nhận (ngoài `count`/`step`/`next`): mục còn thiếu để nêu số + tên. */
export interface ConfirmExtra { assetsDetails?:string; count?: number; step?: WorkflowStepKey; next?: WorkflowStepKey; assetsMissing?: boolean; rerunApproved?: boolean; missingNames?: string[]; missingCount?: number; missingVideos?: number[]; missingWaiting?: number }
export type PrimaryCall =
  | { type: 'STOP'; runId: string }
  | { type: 'RUN_STAGE1'; confirm: ConfirmKind | null; /** P9D: chạy LẠI chặng 1 khi bước 1 đã có kết quả (còn video NEEDS_SPLIT) — nút riêng, không phải nút chính. */ rerun?: true; approved?: boolean; /** CHỈ nút đầu mục «Chạy lại Phân đoạn»: gửi `force:true` để BE viết lại thật. Banner NEEDS_SPLIT không mang cờ này. */ force?: true }
  | { type: 'APPROVE'; step: ApproveStepKey; runNext: boolean; baseHash: string; confirm: ConfirmKind | null; failedCount?: number; confirmCtx?: ConfirmCtx; missing?: { assets?: true; videos?: number[]; waiting?: number } }
  | { type: 'RUN_STEP'; step: RunStepKey; scope: 'missing' | 'failed'; baseRunId?: string; confirm: ConfirmKind | null; count?: number }
  | { type: 'OPEN_STEP'; step: WorkflowStepKey; anchor?: string }
  | { type: 'DOWNLOAD_CAPCUT' };
export interface SecondaryAction { key: string; label: string; enabled: boolean; reason?: string; call: PrimaryCall | null }
export interface PrimaryAction {
  key: PrimaryKey; label: string; enabled: boolean; reason?: string;
  tone: 'brand' | 'dark' | 'danger' | 'muted';
  call: PrimaryCall | null; secondary: SecondaryAction[];
  /** P9Q: nút Duyệt đang khoá vì AI chấm/sửa (trạng thái chờ, không phải lỗi) ⇒ vẽ biểu tượng xoay. */
  waiting?: boolean;
}
export interface PrimaryOpts {
  /** Đang gửi một lệnh ghi ⇒ mọi nút có call tắt (chống bấm đúp). */
  sending?: boolean;
  /** Server vừa trả `NOTHING_TO_DO` cho lượt chạy thiếu của Tài sản (giữ tên cũ). */
  assetsNothingToRun?: boolean;
  /** UI2: các bước mà server vừa trả `NOTHING_TO_DO` ⇒ không đề nghị chạy nữa, sang Duyệt. */
  nothingToRun?: WorkflowStepKey[];
  /**
   * Inventory HIỆN TẠI của bước (đọc `GET …/steps/<k>/subjects`): id các video mà subject hiện tại báo `readiness === 'BLOCKED_UPSTREAM'` (chờ bước trước).
   * Vắng / chưa đọc được ⇒ coi như không video nào chờ (an toàn: vẫn đề nghị Chạy; server trả NOTHING_TO_DO thì không tốn gì).
   */
  blockedUpstream?: Partial<Record<WorkflowStepKey, string[]>>;
}
export interface ConfirmCopy { title: string; content: string; okText: string; cancelText: string }
export interface AssetFlagRow { kind: FlagKind; key: string; /** Tên mục (tên subject) nếu đọc được — thay cho mã ngắn. */ name?: string; typeLabel: string; outcomeLabel: string; message: string; eventIds: string[];
  /** UI-errbox: dữ liệu THÔ của run object để dòng tóm tắt gom nhóm + dịch lý do (chữ thô chỉ vào Tooltip). */ outcome?: string; code?: string; rawMessage?: string; ownerType?: string }
export type StepFlagRow = AssetFlagRow;

const RUN_STEPS: RunStepKey[] = ['ASSETS', 'DIRECTING', 'CINEMATOGRAPHY', 'MAP', 'STORYBOARD', 'PROMPT', 'VIDEO'];
// U32b — Prompt & Video là MỘT ô (khoá đại diện VIDEO): Prompt không còn là bước phải duyệt, Video không đòi Prompt đã duyệt ⇒ cả hai cùng đứng sau Storyboard.
const PREV: Record<RunStepKey, WorkflowStepKey> = { ASSETS: 'SCRIPT', DIRECTING: 'ASSETS', CINEMATOGRAPHY: 'DIRECTING', MAP: 'CINEMATOGRAPHY', STORYBOARD: 'MAP', PROMPT: 'STORYBOARD', VIDEO: 'STORYBOARD' };
/** Chuỗi bước của nút chính (không có PROMPT: nó nằm trong ô gộp VIDEO). */
const CHAIN_STEPS: RunStepKey[] = RUN_STEPS.filter((k) => k !== 'PROMPT');
const NEXT: Partial<Record<WorkflowStepKey, RunStepKey>> = { SCRIPT: 'ASSETS', ASSETS: 'DIRECTING', DIRECTING: 'CINEMATOGRAPHY', CINEMATOGRAPHY: 'MAP', MAP: 'STORYBOARD', STORYBOARD: 'PROMPT', PROMPT: 'VIDEO' };
/** Bước mà lượt chạy tạo ảnh / clip thật ⇒ hộp xác nhận (không giá). Bước chỉ-chữ (Đạo diễn, Quay phim, Prompt) chạy không hỏi. */
const SPENDING: ReadonlySet<WorkflowStepKey> = new Set<WorkflowStepKey>(['ASSETS', 'MAP', 'STORYBOARD', 'VIDEO']);
const SHORT: Partial<Record<WorkflowStepKey, string>> = { SCRIPT: 'Phân đoạn', ASSETS: 'Tài sản', DIRECTING: 'Đạo diễn', CINEMATOGRAPHY: 'Quay phim', MAP: 'Sơ đồ', STORYBOARD: 'Storyboard', PROMPT: 'Prompt', VIDEO: 'Video' };
const shortOf = (k: WorkflowStepKey): string => SHORT[k] ?? stepNameOf(k);
/** Tên ngắn của bước ('Tài sản', 'Đạo diễn', 'Quay phim', 'Sơ đồ'…) cho nhãn / thông báo. */
export const stepShortName = shortOf;
const stepNo = (k: WorkflowStepKey): number => BOARD_STEP_KEYS.indexOf(boardStepKeyOf(k)) + 1;
const isRunStep = (k: WorkflowStepKey): k is RunStepKey => (RUN_STEPS as string[]).includes(k);

const NOT_YET = 'Chưa có ở bản này (lõi đang làm) — dùng ngăn Sửa của từng video.';
const UNAVAILABLE_TEXT = 'Không đọc được tiến độ bước — tải lại trang.';
const OLD_BE_TEXT = 'Máy chủ chưa hỗ trợ duyệt theo bước — cập nhật máy chủ rồi tải lại trang.';

// ───────────────────────── đọc khoá L1–L6 ─────────────────────────

interface StepRun { runId: string; canStop: boolean; stopRequested: boolean; done: number; total: number; failedCount: number; retryableCount: number; notRun: number; canRetry: boolean }
function stepRunOf(s: WorkflowStatus, k: WorkflowStepKey): StepRun | null {
  const r: StepRunView | undefined = s.stepRuns?.[k];
  if (!r || !r.runId) return null;
  const c = r.counts ?? {};
  const p=r.progress?.version===1?r.progress:null;
  const retryable = Number(r.retryableCount ?? c.retryableCount ?? 0) || 0;
  return {
    runId: r.runId, canStop: r.canStop === true, stopRequested: r.stopRequested === true,
    done: p?Number(p.completed)||0:(Number(c.succeeded) || 0) + (Number(c.ready) || 0) + (Number(c.manual) || 0), total: p?Number(p.targetTotal)||0:Number(c.total ?? (r.objects ?? []).length) || 0,
    failedCount: Number(c.failedCount) || 0, retryableCount: retryable, notRun: Number(c.notRun) || 0,
    canRetry: r.canRetryFailed === true || retryable > 0,
  };
}
const scriptGateOf = (s: WorkflowStatus) => s.gates?.steps?.SCRIPT ?? s.gates?.script;
const scriptApprovedOf = (s: WorkflowStatus): boolean =>
  scriptGateOf(s)?.approval?.matches === true || (s.stepCapabilities?.SCRIPT?.supported !== true && (s.steps ?? []).find((x) => x.key === 'SCRIPT')?.state === 'APPROVED');
const stateOfStep = (s: WorkflowStatus, k: WorkflowStepKey) => (s.steps ?? []).find((x) => x.key === k)?.state ?? 'NOT_READY';
const verdictOf = (s: WorkflowStatus, k: RunStepKey) => s.gates?.steps?.[k]?.verdict;
const hashOf = (s: WorkflowStatus, k: RunStepKey): string | undefined => s.gates?.steps?.[k]?.hash;
/** Bước có cổng duyệt thật ở BE (L3–L6): `supported`, độc lập quyền APPROVE hiện tại. BE cũ giữ fallback theo trạng thái video. */
const gatedOf = (s: WorkflowStatus, k: WorkflowStepKey): k is RunStepKey => isRunStep(k) && k !== 'ASSETS' && s.stepCapabilities?.[k]?.supported === true;

/** Bước k "không đọc được nguồn": BE mới liệt kê trong `stepUnavailable`; BE L1 chỉ có cờ chung `stepRunsUnavailable` ⇒ coi mọi bước không đọc được. */
const unavailableOf = (s: WorkflowStatus, k: WorkflowStepKey): boolean =>
  (Array.isArray(s.stepUnavailable) ? s.stepUnavailable.includes(k) : s.stepRunsUnavailable === true);
const globalBlocked = (s: WorkflowStatus): string | null =>
  (!s.stepCapabilities ? OLD_BE_TEXT : (s.stepRunsUnavailable === true && !Array.isArray(s.stepUnavailable)) ? UNAVAILABLE_TEXT : null);

interface EventCounts { total: number; done: number; missing: number; stale: number; error: number; running: number }
function eventCountsOf(s: WorkflowStatus, k: WorkflowStepKey): EventCounts {
  const out: EventCounts = { total: 0, done: 0, missing: 0, stale: 0, error: 0, running: 0 };
  for (const e of s.events ?? []) {
    const v = (e.steps as any)?.[k] ?? 'NOT_READY';
    out.total += 1;
    if (isDoneState(v)) out.done += 1; else if (v === 'NOT_READY') out.missing += 1; else if (v === 'STALE') out.stale += 1; else if (v === 'ERROR') out.error += 1; else if (v === 'RUNNING') out.running += 1;
  }
  return out;
}
/** Thứ tự các video chưa có kết quả ở bước k (thiếu / cũ / lỗi; đang chạy không tính). */
const missingOrdersOf = (s: WorkflowStatus, k: WorkflowStepKey): number[] =>
  (s.events ?? []).filter((e) => ['NOT_READY', 'STALE', 'ERROR'].includes((e.steps as any)?.[k] ?? 'NOT_READY')).map((e) => e.eventOrder).sort((a, b) => a - b);
/**
 * Số video còn THIẾU / CŨ ở bước k mà inventory HIỆN TẠI (subjects) báo `BLOCKED_UPSTREAM` — chạy bước này không làm được gì cho chúng.
 * KHÔNG dùng cờ của lượt chạy cũ (BE không cập nhật cờ BLOCKED theo inventory mới ⇒ bước trước đã xong mà cờ vẫn còn). Không có dữ liệu hiện tại ⇒ 0.
 */
const waitingPrevOf = (s: WorkflowStatus, k: WorkflowStepKey, o: PrimaryOpts): number => {
  const ids = new Set(o.blockedUpstream?.[k] ?? []);
  if (!ids.size) return 0;
  return (s.events ?? []).filter((e) => {
    const st = (e.steps as any)?.[k] ?? 'NOT_READY';
    return (st === 'NOT_READY' || st === 'STALE') && ids.has(e.eventId);
  }).length;
};
/**
 * Từ `GET workflow/steps/<k>/subjects`: id các video đang CHỜ BƯỚC TRƯỚC = có subject HIỆN TẠI `readiness === 'BLOCKED_UPSTREAM'` VÀ không có subject nào chạy được (`MISSING` / `STALE`) chạm tới nó.
 * (Bước Sơ đồ: một video có thể thuộc nhiều subject — sơ đồ bị chặn lẫn sơ đồ / `plan:propose` chạy được — thì vẫn còn việc để chạy.) id lấy từ `eventIds`, hoặc `ownerId` khi chủ thể là video. Không đọc được ⇒ [].
 */
export function blockedEventIdsOf(subjects: Array<{ readiness?: string; eventIds?: string[]; ownerType?: string; ownerId?: string }> | null | undefined): string[] {
  const idsOf = (x: { eventIds?: string[]; ownerType?: string; ownerId?: string }): string[] => [...(x.eventIds ?? []), ...(x.ownerType === 'event' && x.ownerId ? [x.ownerId] : [])];
  const blocked = new Set<string>();
  const runnable = new Set<string>();
  for (const x of Array.isArray(subjects) ? subjects : []) {
    if (!x) continue;
    if (x.readiness === 'BLOCKED_UPSTREAM') idsOf(x).forEach((id) => blocked.add(id));
    else if (x.readiness === 'MISSING' || x.readiness === 'STALE') idsOf(x).forEach((id) => runnable.add(id));
  }
  return Array.from(blocked).filter((id) => !runnable.has(id));
}
const eventsAllDone = (s: WorkflowStatus, k: WorkflowStepKey): boolean => { const c = eventCountsOf(s, k); return c.total > 0 && c.done === c.total; };

/** Durable gate đã qua — prerequisite RUN và nguồn "bước hiện tại"; policy duyệt mới chỉ dùng ở approveBits. */
function stepCompleteOf(s: WorkflowStatus, k: WorkflowStepKey): boolean {
  if (k === 'SCRIPT') return scriptApprovedOf(s);
  if (k === 'ASSETS') return verdictOf(s, 'ASSETS') === 'OK';
  if (gatedOf(s, k)) return verdictOf(s, k) === 'OK';
  return eventsAllDone(s, k);
}

// ───────────────────────── nguồn "xong" ─────────────────────────

export function stepsDoneOf(s: WorkflowStatus): { done: number; total: number; byKey: Partial<Record<WorkflowStepKey, boolean>> } {
  const byKey: Partial<Record<WorkflowStepKey, boolean>> = {};
  for (const k of BOARD_STEP_KEYS) byKey[k]=stepCompleteOf(s,k);
  return { done: BOARD_STEP_KEYS.filter((k) => byKey[k]).length, total: BOARD_STEP_KEYS.length, byKey };
}

export function summaryLineOf(s: WorkflowStatus): string {
  const prog = planProgress(s.steps ?? []);
  const total = prog.total || (s.events ?? []).length;
  const engine = engineChipOf(s.plan?.engines);
  return [`${prog.withClip}/${total} video có clip`, `${stepsDoneOf(s).done}/${BOARD_STEP_KEYS.length} bước xong`, ...(engine ? [engine] : [])].join(' · ');
}

/** Bước mở mặc định: bước đầu chưa xong; xong cả ⇒ Video. */
export function defaultStepOf(s: WorkflowStatus): WorkflowStepKey {
  const d = stepsDoneOf(s).byKey;
  return BOARD_STEP_KEYS.find((k) => !d[k]) ?? 'VIDEO';
}

/** Bước đang hiện ở bảng: bước người dùng chọn (nếu thuộc 8 bước mới) — còn lại bước mặc định. */
export const boardStepOf = (s: WorkflowStatus, selected: WorkflowStepKey | null | undefined): WorkflowStepKey => {
  const k = selected ? boardStepKeyOf(selected) : null; // U32b: ?step=PROMPT (liên kết cũ) mở ô gộp
  return k && BOARD_STEP_KEYS.includes(k) ? k : defaultStepOf(s);
};

/**
 * Lượt chạy gần nhất của bước k DỪNG VÌ HẾT LƯỢT cho phép của một lần chạy (`state BUDGET_STOPPED`, subject `NOT_RUN_BUDGET`) — KHÔNG phải lỗi: còn N mục, bấm Chạy hàng loạt để chạy tiếp.
 * (BE đánh job FAILED không `note` ⇒ `steps[k].state=ERROR` + băng «Máy tự chạy chặng lỗi: không rõ lý do»; FE đọc `stepRuns` để nói đúng.)
 */
export function budgetStopOf(s: WorkflowStatus, k: WorkflowStepKey): { remaining: number } | null {
  const r = s.stepRuns?.[k];
  if (!r) return null;
  const c = r.counts ?? {};
  const p=r.progress?.version===1?r.progress:null;
  const budget = Number(c.budgetBlocked) || 0;
  const stopped = r.state === 'BUDGET_STOPPED' || (r.state === undefined && budget > 0 && !(Number(c.failedCount) > 0));
  return stopped ? { remaining: budget || Number(c.notRun) || 0 } : null;
}
/** Băng cảnh báo JOB_FAILED của lượt dừng vì hết lượt ⇒ thông báo rõ, mức INFO (không đỏ). Không có lượt như vậy ⇒ trả NGUYÊN đối tượng. */
export function withBudgetNotices(s: WorkflowStatus): WorkflowStatus {
  if (!s.alerts?.length || !s.stepRuns) return s;
  let changed = false;
  const alerts = s.alerts.map((a) => {
    if (a.code !== 'JOB_FAILED') return a;
    const k = a.stepKey;
    const b = isRunStep(k) ? budgetStopOf(s, k) : null;
    if (!b || !isRunStep(k)) return a;
    const r = s.stepRuns![k]!;
    // Chỉ hạ alert của ĐÚNG lượt này (khớp jobId). Chữ «không rõ lý do» chỉ là phòng thủ khi BE không gửi `ref.jobId` — có jobId khác ⇒ là job khác (có thể chết thật), giữ nguyên ERROR.
    if (a.ref?.jobId ? a.ref.jobId !== r.runId : !/không rõ lý do/.test(a.message)) return a;
    changed = true;
    const runLabel = k === 'ASSETS' ? 'Chạy tài sản hàng loạt' : `Chạy ${shortOf(k)} hàng loạt`;
    const { ref: _ref, ...rest } = a;
    return { ...rest, code: 'STEP_BUDGET_STOPPED', severity: 'INFO' as const, message: `Dừng vì hết lượt cho phép của một lần chạy — còn ${b.remaining} mục, bấm ${runLabel} để chạy tiếp` };
  });
  return changed ? { ...s, alerts } : s;
}
/**
 * Từ `GET workflow/steps/ASSETS/subjects`: KHÔNG còn mục nào chạy được ⇒ `true`. Chỉ `MISSING_IMAGE` / `STALE` chạy được (đúng `selectRunTargets` của BE); BLOCKED_* / MISSING_BANK / READY thì không.
 * Chưa đọc được (null / không phải mảng) ⇒ `false` = CHƯA BIẾT: giữ hành vi an toàn, không tự đề nghị Duyệt.
 */
export function assetsNothingToRunOf(subjects: Array<{ readiness?: string;optional?:boolean }> | null | undefined): boolean {
  if (!Array.isArray(subjects)) return false;
  return !subjects.some((x) => x && !x.optional && (x.readiness === 'MISSING_IMAGE' || x.readiness === 'STALE'));
}
/** Tên từng mục tài sản theo `key` và theo `ownerType:ownerId` (khoá của run object có thể khác khoá subject). */
export function assetNamesOf(subjects: Array<{ key?: string; ownerType?: string; ownerId?: string; name?: string | null }> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const x of Array.isArray(subjects) ? subjects : []) {
    const name = String(x?.name ?? '').trim();
    if (!x || !name) continue;
    if (x.key) out[x.key] = name;
    if (x.ownerType && x.ownerId) out[`${x.ownerType}:${x.ownerId}`] = name;
  }
  return out;
}

// ───────────────────────── điều kiện chung (nút chính + nút đầu bước) ─────────────────────────

const nothingOf = (k: WorkflowStepKey, o: PrimaryOpts): boolean => (k === 'ASSETS' && o.assetsNothingToRun === true) || (o.nothingToRun ?? []).includes(k);

/** `steps[ASSETS].state` còn mục chưa có ảnh / cũ / lỗi theo inventory HIỆN TẠI (BE tính từ ảnh thật, không phụ thuộc lượt chạy cũ). */
const ASSET_INCOMPLETE = ['NOT_READY', 'STALE', 'ERROR'];
const anyRunning = (s: WorkflowStatus): boolean => RUN_STEPS.some((k) => stepRunOf(s, k)?.canStop === true);
/** Bước HIỆN TẠI của nút chính: kịch bản đã duyệt, bước đầu tiên chưa qua cổng duyệt. */
export const currentRunStepOf = (s: WorkflowStatus): RunStepKey | null => (scriptApprovedOf(s) ? CHAIN_STEPS.find((k) => !stepCompleteOf(s, k)) ?? null : null);

interface RunOffer { retry: PrimaryCall | null; missing: PrimaryCall | null; retryWhy?: string; missingWhy?: string }
/**
 * MỘT policy DUY NHẤT quyết định "bước k có được phát lệnh chạy (hàng loạt / chạy lại lỗi) không" — nút chính (QĐ 3) VÀ nút đầu bước VÀ kiểm lại trước khi gửi dùng chung.
 * Lệnh chạy chỉ được phát cho bước HIỆN TẠI của nút chính (bước đã duyệt, bước chưa tới lượt, đang có lượt chạy, không đọc được ⇒ null cả hai).
 * Nguồn "còn thiếu": Tài sản — lượt chạy gần nhất còn mục chưa chạy (`notRun>0`), chưa có lượt thì `steps[ASSETS].state` ∈ {NOT_READY, STALE, ERROR};
 * bước 3–8 — lượt gần nhất `notRun>0` hoặc còn video thiếu / cũ (chưa có lượt: thêm video lỗi) theo `events[].steps[k]`.
 */
function runOfferOf(s: WorkflowStatus, k: RunStepKey, run: StepRun | null, o: PrimaryOpts): RunOffer {
  const none = (why: string): RunOffer => ({ retry: null, missing: null, retryWhy: why, missingWhy: why });
  const blocked = globalBlocked(s);
  if (blocked) return none(blocked);
  if (unavailableOf(s, k)) return none(UNAVAILABLE_TEXT);
  if (k !== 'ASSETS' && !gatedOf(s, k)) return none(NOT_YET);
  if (anyRunning(s)) return none(run?.canStop ? `Đang chạy ${shortOf(k).toLowerCase()}.` : 'Đang có bước khác chạy — đợi xong hoặc bấm Dừng.');
  if (s.stepCapabilities?.[k]?.canRun !== true) return none(k === 'ASSETS' ? 'Máy chủ chưa cho chạy tài sản.' : `Máy chủ chưa cho chạy ${shortOf(k)}.`);
  if (k === 'ASSETS' ? !scriptApprovedOf(s) : !stepCompleteOf(s, PREV[k])) return none(`Duyệt ${shortOf(PREV[k]).toLowerCase()} trước.`);
  if (k !== 'PROMPT' && verdictOf(s, k) === 'OK') return none(k === 'ASSETS' ? 'Tài sản đã được duyệt.' : `${shortOf(k)} đã được duyệt.`);
  if (currentRunStepOf(s) !== (k === 'PROMPT' ? 'VIDEO' : k)) return none(`Chưa tới lượt ${shortOf(k)} — xong các bước trước đã.`); // U32b: Prompt chạy trong ô gộp (bước hiện tại = VIDEO)
  const out: RunOffer = { retry: null, missing: null };
  if (run && run.canRetry) out.retry = runCall(k, 'failed', run, run.retryableCount || run.failedCount);
  const c = eventCountsOf(s, k);
  const waiting = k !== 'ASSETS' ? waitingPrevOf(s, k, o) : 0;
  const needsRun = k === 'ASSETS'
    ? ((run?.notRun ?? 0) > 0 || ASSET_INCOMPLETE.includes(stateOfStep(s, 'ASSETS'))) // lượt cũ `notRun=0` không biết subject MỚI của inventory hiện tại ⇒ đọc thêm `steps[ASSETS].state`
    : (run ? run.notRun > 0 || c.missing + c.stale - waiting > 0 : c.missing + c.stale + c.error - waiting > 0);
  const machineRun=s.stepCapabilities?.[k]?.approvalPolicy?.mode==='BLOCKED'&&s.stepCapabilities[k]?.approvalPolicy?.primaryAction?.kind==='RUN_MISSING';
  if ((!needsRun || nothingOf(k, o))&&!machineRun) {
    out.missingWhy = k === 'ASSETS' ? approvalBlockerMessage(s.stepCapabilities?.ASSETS)||'Không còn mục tài sản nào thiếu — có thể duyệt.'
      : waiting > 0 && c.missing + c.stale - waiting <= 0 ? `${waiting} video còn thiếu đều đang chờ bước trước (${shortOf(PREV[k as RunStepKey])}) — chạy ${shortOf(k)} không làm được gì; duyệt để đi tiếp, hoặc xử lý bước trước cho các video đó.`
        : `Không còn video nào thiếu ở ${shortOf(k)} — có thể duyệt.`;
  }
  else out.missing = runCall(k, 'missing', run);
  return out;
}
/**
 * U32b — đề nghị chạy của ô gộp «Prompt & Video»: còn video thiếu PROMPT ⇒ chạy PROMPT scope missing trước; prompt đủ ⇒ chạy VIDEO scope missing.
 * Lỗi chạy lại: Prompt trước rồi tới Video. MỘT nguồn cho nút chính, nút đầu bước và kiểm lại trước khi gửi.
 */
function mergedOfferOf(s: WorkflowStatus, o: PrimaryOpts): RunOffer & { retryRun: StepRun | null; retryStep: RunStepKey } {
  const pRun = stepRunOf(s, 'PROMPT');
  const vRun = stepRunOf(s, 'VIDEO');
  const p = runOfferOf(s, 'PROMPT', pRun, o);
  const v = runOfferOf(s, 'VIDEO', vRun, o);
  const promptRetry = !!p.retry;
  return {
    retry: p.retry ?? v.retry, retryRun: promptRetry ? pRun : vRun, retryStep: promptRetry ? 'PROMPT' : 'VIDEO',
    missing: p.missing ?? v.missing, retryWhy: v.retryWhy ?? p.retryWhy, missingWhy: v.missingWhy ?? p.missingWhy,
  };
}
const runOfferFor = (s: WorkflowStatus, k: RunStepKey, o: PrimaryOpts): RunOffer => runOfferOf(s, k, stepRunOf(s, k), o);

/** Trước khi GỬI: lệnh chạy này có còn đúng là thứ policy đang đề nghị theo status MỚI NHẤT không (chống bấm theo màn cũ / hộp xác nhận mở lâu). */
export function runCallOfferedNow(s: WorkflowStatus | null | undefined, call: PrimaryCall, o: PrimaryOpts = {}): boolean {
  if (s && call.type === 'RUN_STAGE1') return call.rerun ? scriptRerunCallOf(s, o) !== null : scriptRunOf(s, o).call !== null;
  if (!s || call.type !== 'RUN_STEP' || !isRunStep(call.step)) return false;
  const offer = runOfferFor(s, call.step, o);
  return call.scope === 'failed' ? offer.retry !== null : offer.missing !== null;
}

/**
 * Chặng 1 "Phân đoạn" (máy tự chạy chặng 1 — `POST workflow/run {stage:1}`): MỘT hàm DUY NHẤT cho nút chính VÀ nút đầu bước 1.
 * Nguồn: `steps[SCRIPT].state` (job phân đoạn đang chạy ⇒ RUNNING; lỗi mới hơn kết quả ⇒ ERROR; đủ thoại + giây ⇒ READY), job `FILM_WORKFLOW_RUN` có `phase: 'stage-1'` (+ `progress`),
 * `workflowRuns.stage1` (lượt đã kết thúc gần nhất). Chỉ đề nghị chạy khi phân đoạn CHƯA sẵn sàng và CHƯA duyệt — không bao giờ gợi ý ghi đè kịch bản có sẵn.
 */
export interface ScriptRunView { phase: 'RUNNING' | 'FAILED' | 'EMPTY' | 'READY'; label: string; call: PrimaryCall | null; why?: string }
const SCRIPT_READY_STATES = ['READY', 'STALE', 'APPROVED'];
function scriptRunOf(s: WorkflowStatus, o: PrimaryOpts): ScriptRunView {
  const st = stateOfStep(s, 'SCRIPT');
  const jobs = s.jobs ?? [];
  if (scriptApprovedOf(s)) return { phase: 'READY', label: 'Phân đoạn', call: null, why: 'Phân đoạn đã được duyệt.' };
  const job = jobs.find((j) => j.type === 'FILM_WORKFLOW_RUN' && j.phase === 'stage-1');
  if (st === 'RUNNING' || job) {
    const p = job?.progress;
    const total = p && Number(p.total) > 0 ? Number(p.total) : 0;
    return {
      phase: 'RUNNING', label: `Đang chạy Phân đoạn…${total ? ` (${p!.current}/${total})` : ''}`, call: null,
      why: runSummaryText(1, undefined, { running: true, progressLabel: p?.label, progressNo: total ? [Number(p!.current), total] : undefined }),
    };
  }
  if (SCRIPT_READY_STATES.includes(st)) return { phase: 'READY', label: 'Phân đoạn', call: null, why: 'Phân đoạn đã sẵn sàng — duyệt phân đoạn, hoặc sửa tay từng video.' };
  const failed = st === 'ERROR' || s.workflowRuns?.stage1?.status === 'FAILED';
  const label = failed ? 'Phân đoạn lại' : 'Phân đoạn';
  const phase = failed ? 'FAILED' : 'EMPTY';
  const kinds = planWriteKindsOf(jobs);
  if (kinds.length) return { phase, label, call: null, why: `Đang chạy lượt AI trên cả plan (${kinds.map(planWriteLabelOf).join(', ')}) — đợi xong rồi chạy.` };
  if (o.sending) return { phase, label, call: null, why: 'Đang gửi yêu cầu — đợi một chút.' };
  return { phase, label, call: { type: 'RUN_STAGE1', confirm: 'RUN_STAGE1' } };
}

/**
 * 261010 P9D (Rev 3 mục 4) — «Chạy lại Phân đoạn» khi bước 1 ĐÃ có kết quả mà còn video vượt trần (máy chia không được).
 * `scriptRunOf` vẫn tắt khi READY (không bao giờ gợi ý ghi đè ở nút chính); đây là nút RIÊNG, người bấm chủ động. Không có kết quả / đang chạy / đang gửi / có việc ghi plan ⇒ null.
 */
export const RERUN_LABEL = 'Chạy lại Phân đoạn';
export const MERGED_RUN_LABEL = 'Chạy Prompt & Video hàng loạt';
export function scriptRerunCallOf(s: WorkflowStatus, o: PrimaryOpts = {}): PrimaryCall | null {
  const st = stateOfStep(s, 'SCRIPT');
  const jobs = s.jobs ?? [];
  if (!(scriptApprovedOf(s) || SCRIPT_READY_STATES.includes(st))) return null;
  if (jobs.some((j) => j.type === 'FILM_WORKFLOW_RUN' && j.phase === 'stage-1')) return null;
  if (planWriteKindsOf(jobs).length) return null;
  if (o.sending) return null;
  return { type: 'RUN_STAGE1', confirm: 'RERUN_STAGE1', rerun: true, approved: scriptApprovedOf(s) };
}

/** Lý do nút «Chạy lại Phân đoạn» đang tắt (chỉ gọi khi bước 1 đã có kết quả mà `scriptRerunCallOf` trả null). */
function scriptRerunWhyOf(s: WorkflowStatus, o: PrimaryOpts): string {
  const jobs = s.jobs ?? [];
  if (jobs.some((j) => j.type === 'FILM_WORKFLOW_RUN' && j.phase === 'stage-1')) return 'Đang chạy Phân đoạn — đợi xong rồi chạy lại.';
  const kinds = planWriteKindsOf(jobs);
  if (kinds.length) return `Đang chạy lượt AI trên cả plan (${kinds.map(planWriteLabelOf).join(', ')}) — đợi xong rồi chạy lại.`;
  if (o.sending) return 'Đang gửi yêu cầu — đợi một chút.';
  return 'Chưa chạy lại Phân đoạn được lúc này.';
}

interface ActionBits { enabled: boolean; reason?: string; call: PrimaryCall | null }
function scriptApproveBits(s: WorkflowStatus): ActionBits {
  const qw = qualityWaitOf(s, 'SCRIPT');
  if (qw.waiting) return { enabled: false, reason: qw.text!, call: null };
  const gate = scriptGateOf(s);
  const st = stateOfStep(s, 'SCRIPT');
  if (!approvalOffered(s.stepCapabilities?.SCRIPT)) return { enabled: false, reason: 'Máy chủ chưa cho duyệt phân đoạn của plan này.', call: null };
  if (!gate?.hash) return { enabled: false, reason: 'Chưa đọc được mã phân đoạn — tải lại trang.', call: null };
  if (st === 'NOT_READY' || st === 'RUNNING' || st === 'ERROR') return { enabled: false, reason: 'Phân đoạn chưa xong.', call: null };
  return { enabled: true, call: { type: 'APPROVE', step: 'SCRIPT', runNext: true, baseHash: gate.hash, confirm: 'APPROVE_SCRIPT' } };
}

/** Duyệt bước k (≥ Tài sản). `runNext` theo `canRunNext` của BE (VIDEO không có bước kế ⇒ false). Hộp xác nhận chỉ khi còn mục lỗi để đó hoặc bước kế tạo ảnh / clip thật. */
function approveBits(s: WorkflowStatus, k: RunStepKey, run: StepRun | null, o: PrimaryOpts = {}): ActionBits {
  const qw = qualityWaitOf(s, k);
  if (qw.waiting) return { enabled: false, reason: qw.text!, call: null }; // AI đang chấm/sửa: chờ, không phải lỗi — đứng TRƯỚC mọi lý do khác
  const cap = s.stepCapabilities?.[k];
  const name = shortOf(k).toLowerCase();
  if (!cap || !approvalOffered(cap)) return { enabled: false, reason: approvalBlockerMessage(cap)||'Tải lại trạng thái để xem việc bắt buộc còn thiếu.', call: null };
  if (unavailableOf(s, k)) return { enabled: false, reason: UNAVAILABLE_TEXT, call: null };
  const hash = hashOf(s, k);
  if (!hash) return { enabled: false, reason: `Chưa đọc được mã ${name} — tải lại trang.`, call: null };
  if (k === 'ASSETS' ? !scriptApprovedOf(s) : !stepCompleteOf(s, PREV[k])) return { enabled: false, reason: `Duyệt ${shortOf(PREV[k]).toLowerCase()} trước.`, call: null };
  if (k !== 'ASSETS') {
    const c = eventCountsOf(s, k);
    if (c.done === 0 && !run) return { enabled: false, reason: `Chưa có kết quả ${name} — chạy bước này trước.`, call: null };
  }
  const failedCount = run?.failedCount ?? 0;
  const next = NEXT[k];
  const runNext = k!=='PROMPT' && cap.canRunNext === true && !!next;
  // Còn mục CHƯA CÓ KẾT QUẢ (tài sản chưa đủ ảnh / video thiếu-cũ-lỗi) mà vẫn duyệt (vd. máy báo NOTHING_TO_DO) ⇒ hộp xác nhận phải nói rõ còn bao nhiêu mục.
  const missing: NonNullable<Extract<PrimaryCall, { type: 'APPROVE' }>['missing']> | undefined = k === 'ASSETS'
    ? (ASSET_INCOMPLETE.includes(stateOfStep(s, 'ASSETS')) ? { assets: true } : undefined)
    : (() => { const v = missingOrdersOf(s, k); const w = waitingPrevOf(s, k, o); return v.length ? { videos: v, ...(w > 0 ? { waiting: w } : {}) } : undefined; })();
  if (k === 'ASSETS') return { enabled: true, call: { type: 'APPROVE', step: 'ASSETS', runNext, baseHash: hash, confirm: failedCount > 0 || missing ? 'APPROVE_ASSETS' : null, failedCount, ...(missing ? { missing } : {}) } };
  const spends = runNext && !!next && SPENDING.has(next);
  return {
    enabled: true,
    call: { type: 'APPROVE', step: k, runNext, baseHash: hash, confirm: failedCount > 0 || spends || missing ? 'APPROVE_STEP' : null, failedCount, confirmCtx: { step: k, ...(runNext && next ? { next } : {}) }, ...(missing ? { missing } : {}) },
  };
}

const runCall = (k: RunStepKey, scope: 'missing' | 'failed', run: StepRun | null, n?: number): PrimaryCall => {
  const spends = SPENDING.has(k);
  const confirm: ConfirmKind | null = k === 'ASSETS' ? (scope === 'failed' ? 'RUN_FAILED' : 'RUN_MISSING') : spends ? (scope === 'failed' ? 'RUN_STEP_FAILED' : 'RUN_STEP_MISSING') : null;
  return scope === 'failed'
    ? { type: 'RUN_STEP', step: k, scope: 'failed', baseRunId: run!.runId, confirm, count: n }
    : { type: 'RUN_STEP', step: k, scope: 'missing', confirm };
};
const retryAction = (k: RunStepKey, run: StepRun, offer: RunOffer): SecondaryAction => {
  const label = `Chạy lại ${run.retryableCount || run.failedCount} lỗi`;
  return offer.retry ? { key: 'RUN_FAILED', label, enabled: true, call: offer.retry } : { key: 'RUN_FAILED', label, enabled: false, reason: offer.retryWhy ?? 'Chưa chạy lại được.', call: null };
};

// ───────────────────────── ô bước trên thanh ─────────────────────────

/** Chữ ngắn của ô bước khi AI chấm/sửa: «AI đang chấm» · «AI đang sửa 2/3». */
const qualityBadgeText = (qw: ReturnType<typeof qualityWaitOf>): string => (qw.kind === 'FIXING' ? `AI đang sửa ${Math.min(Math.max(qw.activity.round, 1), qw.activity.maxRounds)}/${qw.activity.maxRounds}` : 'AI đang chấm');

/** Chữ + tông của ô bước (theo v7: "chờ duyệt" · "đã duyệt" · "đang chạy 2/9" · "2 lỗi"). */
export function stepBadgeOf(s: WorkflowStatus, key: WorkflowStepKey): StepBadge {
  const sum = key === 'VIDEO' ? (mergedSummaryOf(s.steps) ?? (s.steps ?? []).find((x) => x.key === key)) : (s.steps ?? []).find((x) => x.key === key); // U32b: ô 7 gộp Prompt + Video
  const dflt = (): StepBadge => { const st = sum?.state ?? 'NOT_READY'; return { text: STATE_META[st].label, tone: STATE_META[st].tone }; };
  if (key === 'SCRIPT') {
    if (scriptApprovedOf(s)) return { text: 'đã duyệt', tone: 'ok' };
    const qw = qualityWaitOf(s, 'SCRIPT');
    if (qw.waiting && sum?.state === 'READY') return { text: qualityBadgeText(qw), tone: 'doing' };
    return sum?.state === 'READY' ? { text: 'chờ duyệt', tone: 'doing' } : dflt();
  }
  if (key === 'ASSETS' || gatedOf(s, key)) {
    const k = key as RunStepKey;
    const run = stepRunOf(s, k);
    const v = verdictOf(s, k);
    const pRun = k === 'VIDEO' ? stepRunOf(s, 'PROMPT') : null; // U32b: lượt Prompt chạy / lỗi cũng hiện ở ô gộp
    if (run?.canStop) return { text: `đang chạy ${run.done}/${run.total}`, tone: 'doing' };
    if (pRun?.canStop) return { text: `đang chạy ${pRun.done}/${pRun.total}`, tone: 'doing' };
    if (run?.canRetry) return { text: `${run.retryableCount || run.failedCount} lỗi`, tone: 'bad' };
    if (pRun?.canRetry) return { text: `${pRun.retryableCount || pRun.failedCount} lỗi`, tone: 'bad' };
    if (v !== 'OK') { const qw = qualityWaitOf(s, k); if (qw.waiting) return { text: qualityBadgeText(qw), tone: 'doing' }; }
    const bs = v === 'OK' ? null : budgetStopOf(s, k);
    if (bs) return { text: `dừng · còn ${bs.remaining}`, tone: 'warn' }; // hết lượt của một lần chạy — không phải lỗi
    if (v === 'OK') return { text: 'đã duyệt', tone: 'ok' };
    if (v === 'OUTDATED') return { text: 'cần duyệt lại', tone: 'warn' };
    if (k === 'ASSETS') return sum?.state === 'READY' ? { text: 'chờ duyệt', tone: 'doing' } : dflt();
    if (eventsAllDone(s, k)) return { text: 'chờ duyệt', tone: 'doing' };
  }
  if (!sum) return dflt();
  // Bước cấp video: chữ NGẮN (vừa ô 8 cột) — lỗi > cũ > đang chạy > xong cả > x/y > chưa tới.
  const done = sum.ready + sum.approved;
  if (sum.failed > 0) return { text: `${sum.failed} lỗi`, tone: 'bad' };
  if (sum.stale > 0) return { text: `${sum.stale} cũ`, tone: 'warn' };
  if (sum.running > 0) return { text: `đang chạy ${sum.running}`, tone: 'doing' };
  if (sum.total > 0 && done >= sum.total) return { text: 'xong', tone: 'ready' };
  if (done > 0) return { text: `${done}/${sum.total}`, tone: 'ready' };
  return dflt();
}

/** Cờ ERROR của ĐÚNG lượt đang chạy trên video này (`stepFlags[].runId` khớp `stepRuns[k].runId`). */
function thisRunErrorOf(s: WorkflowStatus, key: WorkflowStepKey, e: { eventId: string; stepFlags?: WorkflowStatus['events'][number]['stepFlags'] }): boolean {
  const run = s.stepRuns?.[key];
  if (!run?.runId || !run.canStop) return false;
  const flags = ((e.stepFlags?.[key] ?? []) as Array<{ subjectKey: string; runId: string; code: string }>).filter((f) => f.runId === run.runId);
  if (!flags.length) return false;
  return eventFlagInfosOf(key, { eventId: e.eventId, stepFlags: { [key]: flags } }, run.objects).some((i) => i.kind === 'ERROR');
}

/** Dải đoạn màu dưới ô bước: mỗi video một đoạn. Bước có cờ lỗi bền (`events[].stepFlags[k]`) đánh đỏ video bị cờ — còn sau F5. Đang chạy (`canStop`) ⇒ không `bad` trừ cờ ERROR của lượt này. */
export function stepSegmentsOf(s: WorkflowStatus, key: WorkflowStepKey): Array<{ eventId: string; tone: StateTone; label: string }> {
  const events = [...(s.events ?? [])].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
  const badge = stepBadgeOf(s, key);
  const running = stepRunOf(s, key)?.canStop === true || (key === 'VIDEO' && stepRunOf(s, 'PROMPT')?.canStop === true);
  return events.map((e) => {
    if (key === 'SCRIPT') return { eventId: e.eventId, tone: badge.tone, label: `Video ${e.eventOrder} — ${badge.text}` };
    if (running) {
      const bad = thisRunErrorOf(s, key, e);
      return { eventId: e.eventId, tone: bad ? 'bad' : 'doing', label: `Video ${e.eventOrder} — ${bad ? 'có lỗi lượt chạy' : badge.text}` };
    }
    // Chỉ cờ LỖI mới tô đỏ; cờ "giữ sửa tay" (SKIPPED_MANUAL) là thông tin, giữ màu trạng thái.
    const infos = eventFlagInfosOf(key, e, s.stepRuns?.[key]?.objects);
    const err = infos.find((i) => i.kind === 'ERROR');
    const kept = infos.some((i) => i.kind === 'KEPT');
    const flagged = !!err;
    if (key === 'ASSETS') {
      const st = stateOfStep(s, 'ASSETS');
      const calm: StateTone = verdictOf(s, 'ASSETS') === 'OK' ? 'ok' : (st === 'ERROR' && budgetStopOf(s, 'ASSETS')) ? 'warn' : STATE_META[st].tone; // video KHÔNG có cờ không bị tô theo lỗi của video khác
      return { eventId: e.eventId, tone: flagged ? 'bad' as const : calm, label: `Video ${e.eventOrder} — ${flagged ? 'có mục tài sản lỗi' : kept ? `${badge.text} · giữ sửa tay` : badge.text}` };
    }
    const st = key === 'VIDEO' ? mergedStateOf(e.steps?.PROMPT, e.steps?.VIDEO) : ((e.steps as any)?.[key] ?? 'NOT_READY'); // U32b: gộp trạng thái Prompt + Video của video
    const approved = gatedOf(s, key) && verdictOf(s, key) === 'OK' && isDoneState(st);
    const tone: StateTone = flagged ? 'bad' : approved ? 'ok' : STATE_META[st as keyof typeof STATE_META].tone;
    const calmLabel = approved ? 'đã duyệt' : STATE_META[st as keyof typeof STATE_META].label;
    return { eventId: e.eventId, tone, label: `Video ${e.eventOrder} — ${flagged ? (err!.label === 'lỗi' ? 'có lỗi lượt chạy' : err!.label) : kept ? `${calmLabel} · giữ sửa tay` : calmLabel}` };
  });
}

// ───────────────────────── nút chính (QĐ 3, tổng quát hoá cho bước 3–8) ─────────────────────────

export function primaryActionOf(s: WorkflowStatus, o: PrimaryOpts = {}): PrimaryAction {
  const fin = (a: PrimaryAction): PrimaryAction => (o.sending && a.call ? { ...a, enabled: false } : a);
  const mk = (key: PrimaryKey, label: string, tone: PrimaryAction['tone'], bits: ActionBits, secondary: SecondaryAction[] = []): PrimaryAction =>
    fin({ key, label, tone, enabled: bits.enabled, ...(bits.reason ? { reason: bits.reason } : {}), call: bits.call, secondary });

  // 1. kịch bản chưa sẵn sàng (chưa viết / đang viết / lỗi) ⇒ nút chính là VIẾT kịch bản — chặng 1 không phụ thuộc dữ liệu L1 nên đứng trước cả `globalBlocked`.
  const sr = scriptRunOf(s, o);
  if (sr.phase !== 'READY') return mk('RUN_SCRIPT', sr.label, sr.phase === 'RUNNING' ? 'dark' : 'brand', { enabled: !!sr.call, ...(sr.why ? { reason: sr.why } : {}), call: sr.call });

  const blocked = globalBlocked(s);
  if (blocked) return mk('UNAVAILABLE', 'Chưa thao tác được', 'muted', { enabled: false, reason: blocked, call: null });

  // 2. có lượt chạy đang chạy (bất kỳ bước) ⇒ Dừng (ưu tiên hơn mọi duyệt).
  for (const k of ['SCRIPT',...RUN_STEPS] as WorkflowStepKey[]) {
    const run = stepRunOf(s, k);
    if (!run?.canStop) continue;
    const txt = `${shortOf(k)} · ${run.done}/${run.total}`;
    return run.stopRequested
      ? mk('STOP', `Đang dừng ${txt}…`, 'dark', { enabled: false, reason: 'Đã yêu cầu dừng — máy sẽ dừng sau mục đang tạo.', call: null })
      : mk('STOP', `Đang chạy ${txt} (Dừng)`, 'dark', { enabled: true, call: { type: 'STOP', runId: run.runId } });
  }

  // 3. kịch bản chưa duyệt (hoặc duyệt cũ đã lệch).
  if (!scriptApprovedOf(s)) {
    const label = scriptGateOf(s)?.approval ? 'Duyệt lại phân đoạn' : 'Duyệt phân đoạn';
    const a = mk('APPROVE_SCRIPT', label, 'brand', scriptApproveBits(s));
    return qualityWaitOf(s, 'SCRIPT').waiting ? { ...a, waiting: true } : a;
  }

  // 4–7. bước đầu tiên chưa xong quyết định nút chính (U32b: 7 bước — Prompt & Video là một ô, khoá đại diện VIDEO).
  for (const k of CHAIN_STEPS) {
    if (stepCompleteOf(s, k)) continue;
    // BE báo bước này không đọc được nguồn (capability của nó bị tắt kèm): nói đúng lý do, không phải "chưa có ở bản này".
    if (unavailableOf(s, k)) return mk('UNAVAILABLE', 'Chưa thao tác được', 'muted', { enabled: false, reason: UNAVAILABLE_TEXT, call: null });
    // BE cũ (bước 3–8 chưa có cổng): hiện nhưng tắt kèm chú thích.
    const approvalPolicy=s.stepCapabilities?.[k]?.approvalPolicy;
    const action=approvalPolicy?.primaryAction;
    if(approvalPolicy?.mode==='BLOCKED'&&action&&!anyRunning(s)&&!planWriteKindsOf(s.jobs).length){
      if(action.kind==='OPEN_UPSTREAM')return mk('OPEN_FAILED','Xử lý ở bước trước','muted',{enabled:true,call:{type:'OPEN_STEP',step:action.stepKey as WorkflowStepKey}});
    }
    if (k !== 'ASSETS' && !gatedOf(s, k)) return mk('NEXT_DISABLED', `Duyệt ${stepNameOf(k)}`, 'muted', { enabled: false, reason: NOT_YET, call: null });
    const run = stepRunOf(s, k);
    const merged = k === 'VIDEO' ? mergedOfferOf(s, o) : null;
    const offer = merged ?? runOfferOf(s, k, run, o);
    // P9Q: AI đang chấm/sửa bước này (chưa duyệt) ⇒ nút chính = Duyệt khoá với đúng câu chờ, đứng trước mọi đề nghị chạy.
    if (qualityWaitOf(s, k).waiting && verdictOf(s, k) !== 'OK') {
      const lbl = `${verdictOf(s, k) === 'OUTDATED' ? 'Duyệt lại' : 'Duyệt'} ${shortOf(k)}`;
      return { ...mk(k === 'ASSETS' ? 'APPROVE_ASSETS' : 'APPROVE_STEP', lbl, 'brand', approveBits(s, k, run, o)), waiting: true };
    }
    // còn mục lỗi chạy lại được ⇒ mở bước đó + phụ "Chạy lại N lỗi".
    const retryRun = merged ? merged.retryRun : run;
    if (retryRun && retryRun.canRetry) {
      const n = retryRun.retryableCount || retryRun.failedCount;
      return mk('OPEN_FAILED', `Xử lý ${n} lỗi ở bước ${stepNo(k)}`, 'danger', { enabled: true, call: { type: 'OPEN_STEP', step: k, anchor: 'wf-step-flags' } }, [retryAction(merged ? merged.retryStep : k, retryRun, offer)]);
    }
    // còn mục thiếu ⇒ chạy hàng loạt (ô gộp: Prompt còn thiếu thì chạy Prompt trước, rồi tới Video).
    if (offer.missing) {
      return mk('RUN_MISSING', k === 'ASSETS' ? 'Chạy tài sản hàng loạt' : k === 'VIDEO' ? MERGED_RUN_LABEL : `Chạy ${shortOf(k)} hàng loạt`, 'brand', { enabled: true, call: offer.missing });
    }
    // duyệt.
    const v = verdictOf(s, k);
    const label = k === 'ASSETS' ? (v === 'OUTDATED' ? 'Duyệt lại tài sản' : 'Duyệt tài sản') : `${v === 'OUTDATED' ? 'Duyệt lại' : 'Duyệt'} ${shortOf(k)}`;
    const sec: SecondaryAction[] = k === 'VIDEO' ? [{ key: 'DOWNLOAD', label: 'Tải clip cho CapCut', enabled: true, call: { type: 'DOWNLOAD_CAPCUT' } }] : [];
    return mk(k === 'ASSETS' ? 'APPROVE_ASSETS' : 'APPROVE_STEP', label, 'brand', approveBits(s, k, run, o), sec);
  }

  // 8. đủ cả 8 bước ⇒ tải clip.
  return mk('DOWNLOAD_CAPCUT', 'Tải clip cho CapCut', 'brand', { enabled: true, call: { type: 'DOWNLOAD_CAPCUT' } });
}

/** Nút ở đầu phần một bước (theo v7: "Chạy hàng loạt" · "Chạy lại N lỗi" · "Duyệt bước này"). Cùng điều kiện với nút chính. */
export function stepHeaderActionsOf(s: WorkflowStatus, key: WorkflowStepKey, o: PrimaryOpts = {}): SecondaryAction[] {
  const sending = !!o.sending;
  const blocked = globalBlocked(s);
  const off = (k: string, label: string, reason: string): SecondaryAction => ({ key: k, label, enabled: false, reason, call: null });
  const gate = (a: SecondaryAction): SecondaryAction => (sending && a.call ? { ...a, enabled: false } : a);
  if (key === 'SCRIPT') {
    // CÙNG hàm `scriptRunOf` với nút chính: nút "Phân đoạn" bật ⇔ nút chính đề nghị viết.
    const sr = scriptRunOf(s, o);
    // Bước 1 ĐÃ có kết quả (READY/STALE/đã duyệt) ⇒ nút đầu mục là «Chạy lại Phân đoạn» (nút RIÊNG, người bấm chủ động; hộp xác nhận RERUN_STAGE1). Nút chính KHÔNG đổi.
    const rr0 = sr.phase === 'READY' ? scriptRerunCallOf(s, o) : null;
    const rr = rr0 && rr0.type === 'RUN_STAGE1' ? ({ ...rr0, force: true } as const) : null; // CHỈ nút đầu mục mang force (banner dùng thẳng scriptRerunCallOf, không force)
    const write: SecondaryAction = sr.phase === 'READY'
      ? (rr ? { key: 'RERUN_STAGE1', label: RERUN_LABEL, enabled: true, call: rr } : off('RERUN_STAGE1', RERUN_LABEL, scriptRerunWhyOf(s, o)))
      : { key: 'RUN_STAGE1', label: sr.label, enabled: !!sr.call, ...(sr.call ? {} : { reason: sr.why }), call: sr.call };
    if (scriptApprovedOf(s)) return [write, off('APPROVE', 'Duyệt bước này', 'Phân đoạn đã được duyệt.')];
    if (sr.phase !== 'READY') return [write, off('APPROVE', 'Duyệt bước này', sr.phase === 'RUNNING' ? 'Đang chạy Phân đoạn — đợi xong rồi duyệt.' : 'Phân đoạn chưa xong — bấm Phân đoạn trước.')];
    if (blocked) return [write, off('APPROVE', 'Duyệt bước này', blocked)];
    const b = scriptApproveBits(s); // đang chờ AI chấm ⇒ tắt kèm câu chờ
    return [write, gate({ key: 'APPROVE', label: 'Duyệt bước này', enabled: b.enabled, reason: b.reason, call: b.call })];
  }
  if (!isRunStep(key) || key === 'PROMPT') return []; // U32b: Prompt không còn bước/nút riêng — nằm trong ô gộp VIDEO
  if (unavailableOf(s, key)) return [off('RUN_BATCH', 'Chạy hàng loạt', UNAVAILABLE_TEXT), off('APPROVE', 'Duyệt bước này', UNAVAILABLE_TEXT)];
  // BE cũ: bước 3–8 chưa có cổng ⇒ hiện nhưng tắt.
  if (key !== 'ASSETS' && !gatedOf(s, key)) return [off('RUN_BATCH', 'Chạy hàng loạt', NOT_YET), off('APPROVE', 'Duyệt bước này', NOT_YET)];
  const run = stepRunOf(s, key);
  const merged = key === 'VIDEO' ? mergedOfferOf(s, o) : null;
  const offer = merged ?? runOfferOf(s, key, run, o);
  const why = blocked ?? (anyRunning(s) ? (run?.canStop ? `Đang chạy ${shortOf(key).toLowerCase()}.` : 'Đang có bước khác chạy — đợi xong hoặc bấm Dừng.') : null);
  const out: SecondaryAction[] = [];
  const retryRun = merged ? merged.retryRun : run;
  if (retryRun && retryRun.canRetry) out.push(gate(retryAction(merged ? merged.retryStep : key, retryRun, offer)));
  const label = key === 'ASSETS' ? 'Chạy tài sản hàng loạt' : 'Chạy hàng loạt';
  out.push(offer.missing ? gate({ key: 'RUN_MISSING', label, enabled: true, call: offer.missing }) : off('RUN_MISSING', label, offer.missingWhy ?? offer.retryWhy ?? 'Máy chủ chưa cho chạy.'));
  const qw = qualityWaitOf(s, key);
  if (why) out.push(off('APPROVE', 'Duyệt bước này', why));
  else if (qw.waiting && verdictOf(s, key) !== 'OK') out.push(off('APPROVE', 'Duyệt bước này', qw.text!));
  else if (verdictOf(s, key) === 'OK') out.push(off('APPROVE', 'Duyệt bước này', key === 'ASSETS' ? 'Tài sản đã được duyệt.' : `${shortOf(key)} đã được duyệt.`));
  else { const b = approveBits(s, key, run, o); out.push(gate({ key: 'APPROVE', label: 'Duyệt bước này', enabled: b.enabled, reason: b.reason, call: b.call })); }
  return out;
}

/** Từ `GET workflow/steps/ASSETS/subjects`: các mục CHƯA CÓ ẢNH (readiness khác READY) — số lượng + tên (thiếu tên ⇒ khoá). */
export function missingAssetsOf(subjects: Array<{ key?: string; name?: string | null; readiness?: string;optional?:boolean }> | null | undefined): { count: number; names: string[] } {
  const rows = (Array.isArray(subjects) ? subjects : []).filter((x) => x && !x.optional && x.readiness && x.readiness !== 'READY');
  const names = Array.from(new Set(rows.map((x) => String(x.name ?? '').trim() || String(x.key ?? '')).filter(Boolean)));
  return { count: rows.length, names };
}

// ───────────────────────── hộp xác nhận (không giá) ─────────────────────────

const SPEND_TEXT: Partial<Record<WorkflowStepKey, string>> = { ASSETS: 'tạo ảnh thật', MAP: 'tạo ảnh thật', STORYBOARD: 'tạo ảnh thật', VIDEO: 'tạo clip thật, có thể mất vài phút' };

export function confirmCopyOf(kind: ConfirmKind, ctx: ConfirmExtra = {}): ConfirmCopy | null {
  const n = Number.isFinite(ctx.count) ? Number(ctx.count) : 0;
  const ok = { okText: 'Xác nhận', cancelText: 'Huỷ' };
  switch (kind) {
    case 'APPROVE_SCRIPT':
      return { title: 'Duyệt phân đoạn?', content: 'Máy sẽ tự chạy tài sản (tạo ảnh thật) cho các mục còn thiếu của plan này, rồi dừng chờ bạn duyệt.', ...ok };
    case 'RUN_STAGE1':
      return { title: 'Phân đoạn?', content: 'Máy viết thoại, chốt địa điểm và thời lượng cho mọi video. Chỉ dùng AI chữ.', ...ok };
    case 'RERUN_STAGE1':
      return { title: 'Chạy lại Phân đoạn?', content: `Máy viết lại toàn bộ video của Phân đoạn từ đầu: thoại, địa điểm, thời lượng làm lại, các video hiện có bị ghi đè và mọi chỉnh sửa tay của bạn sẽ mất. Video nào chia được thì máy chia, không thì bạn cắt tay (nút Cắt đôi ở từng video). Chỉ dùng AI chữ.${ctx.rerunApproved ? ' Phân đoạn đã duyệt — chạy lại rồi duyệt Phân đoạn sẽ thành cũ, mất duyệt hiện tại, phải duyệt lại.' : ''}`, ...ok };
    case 'RUN_MISSING':
      return { title: 'Chạy tài sản hàng loạt?', content: 'Chạy tài sản hàng loạt cho các mục còn thiếu. Tạo ảnh thật, có thể mất vài phút.', ...ok };
    case 'RUN_FAILED':
      return { title: n > 0 ? `Chạy lại ${n} lỗi?` : 'Chạy lại các mục lỗi?', content: `${n > 0 ? `Chạy lại ${n} mục lỗi.` : 'Chạy lại các mục lỗi.'} Tạo ảnh thật, có thể mất vài phút.`, ...ok };
    case 'APPROVE_ASSETS': {
      const parts: string[] = [];
      if (ctx.assetsMissing) {
        const names = (ctx.missingNames ?? []).filter(Boolean);
        const total = Number(ctx.missingCount) > 0 ? Number(ctx.missingCount) : names.length;
        if (total > 0 && names.length) {
          const shown = names.slice(0, 6); const more = total - shown.length;
          parts.push(`Còn ${total} mục chưa có ảnh: ${shown.join(', ')}${more > 0 ? ` và ${more} mục nữa` : ''}. Duyệt xong các mục này vẫn thiếu ảnh.`);
        } else parts.push('Tài sản chưa đủ ảnh (chưa đọc được tên các mục). Duyệt xong các mục thiếu vẫn không có ảnh.');
      }
      if (n > 0) parts.push(`${n} mục lỗi sẽ để đó, chạy lại được sau.`);
      return parts.length ? { title: 'Duyệt tài sản?', content: parts.join(' '), ...ok } : null;
    }
    case 'RUN_STEP_MISSING': case 'RUN_STEP_FAILED': {
      const k = ctx.step; if (!k) return null;
      const failed = kind === 'RUN_STEP_FAILED';
      const what = failed ? (n > 0 ? `Chạy lại ${n} video lỗi ở ${shortOf(k)}` : `Chạy lại các video lỗi ở ${shortOf(k)}`) : `Chạy ${shortOf(k)} hàng loạt cho các video còn thiếu`;
      return { title: `${failed ? (n > 0 ? `Chạy lại ${n} lỗi` : 'Chạy lại các lỗi') : `Chạy ${shortOf(k)} hàng loạt`}?`, content: `${what}. ${(SPEND_TEXT[k] ?? 'Chạy ở nền').replace(/^./, (c) => c.toUpperCase())}.`, ...ok };
    }
    case 'APPROVE_STEP': {
      const k = ctx.step; if (!k) return null;
      const parts: string[] = [];
      if (ctx.next && SPENDING.has(ctx.next)) parts.push(`Máy sẽ tự chạy ${shortOf(ctx.next)} cho các video còn thiếu (${SPEND_TEXT[ctx.next]}), rồi dừng chờ bạn duyệt.`);
      if (ctx.missingVideos?.length) parts.push(`Còn ${ctx.missingVideos.length} video chưa có kết quả: ${ctx.missingVideos.map((o) => `Video ${o}`).join(', ')}.${ctx.missingWaiting ? ` ${ctx.missingWaiting} video đang chờ bước trước.` : ''}`);
      if (n > 0) parts.push(`${n} video lỗi sẽ để đó, chạy lại được sau.`);
      return parts.length ? { title: `Duyệt ${shortOf(k)}?`, content: parts.join(' '), ...ok } : null;
    }
    default:
      return null;
  }
}

/** Câu báo NGAY khi lệnh ghi trả OK (trước khi tải lại status chậm): duyệt có chạy tiếp ⇒ «Đã duyệt X — đang chạy <bước kế>». Không có câu ⇒ null. */
export function successNoticeOf(call: PrimaryCall, resp?: { started?: boolean; jobId?: string } | null): string | null {
  if (call.type === 'APPROVE') {
    const name = shortOf(call.step);
    const next = NEXT[call.step];
    return call.runNext && next && resp?.started !== false ? `Đã duyệt ${name} — đang chạy ${shortOf(next)}` : `Đã duyệt ${name}`;
  }
  if (call.type === 'RUN_STEP') return call.scope === 'failed' ? `Đã gửi lệnh chạy lại lỗi ${shortOf(call.step)} — chạy ở nền, tiến độ hiện ở thanh bước.` : `Đã gửi lệnh chạy ${shortOf(call.step)} — chạy ở nền, tiến độ hiện ở thanh bước.`;
  return null;
}

// ───────────────────────── lỗi L1–L6 ─────────────────────────

export function l1ActionError(err: any, what: string): ActionError {
  const d = err?.response?.data;
  // Hai dạng body: `{code, message}` (controller) và `{errorCode, code, messages[]}` (restInterceptor). Mã đọc từ `code` hoặc `errorCode`; câu server đọc cả `message` lẫn `messages[0]`.
  const code = typeof d?.code === 'string' ? d.code : typeof d?.errorCode === 'string' ? d.errorCode : '';
  const mk = (kind: ActionError['kind'], text: string): ActionError => ({ kind, text: `${what}: ${text}`, reload: true });
  switch (code) {
    case 'SCRIPT_NOT_APPROVED': return mk('SCRIPT_GATE', 'chưa duyệt phân đoạn — duyệt phân đoạn trước.');
    case 'SCRIPT_APPROVAL_OUTDATED': return mk('SCRIPT_GATE', 'phân đoạn đã đổi sau lần duyệt — duyệt lại phân đoạn.');
    case 'PREVIOUS_STEP_NOT_APPROVED': return mk('SCRIPT_GATE', `bước trước${d?.details?.step ? ` (${shortOf(d.details.step)})` : ''} chưa được duyệt — duyệt bước trước rồi chạy.`);
    case 'PREVIOUS_STEP_OUTDATED': return mk('SCRIPT_GATE', `bước trước${d?.details?.step ? ` (${shortOf(d.details.step)})` : ''} đã đổi sau lần duyệt — duyệt lại bước trước.`);
    case 'STALE_BASE_RUN': return mk('CHANGED', 'lượt chạy này đã cũ (có lượt mới hơn) — đã tải lại, hãy xem lại rồi bấm lại.');
    case 'IDEMPOTENCY_CONFLICT': return mk('CHANGED', 'yêu cầu này trùng mã nhưng khác nội dung — đã tải lại, hãy bấm lại một lần.');
    case 'SUBJECT_CHANGED': return mk('CHANGED', 'dữ liệu vừa đổi ở nơi khác — đã tải lại, hãy xem lại rồi duyệt lại.');
    case 'NEXT_STEP_NOT_IMPLEMENTED': return mk('OTHER', 'chưa có bước kế để chạy tiếp — chỉ duyệt thôi.');
    case 'STEP_NOT_IMPLEMENTED': return mk('OTHER', 'bước này chưa duyệt / chạy được ở bản này.');
    case 'WORKFLOW_STATE_UNAVAILABLE': return mk('OTHER', 'không đọc được trạng thái bước ở máy chủ — thử lại sau.');
    case 'LEGACY_RUN_NOT_STOPPABLE': return mk('OTHER', 'lượt chạy này không dừng được từ đây.');
    case 'NO_ACTIVE_APPROVAL': return mk('SCRIPT_GATE', 'chưa có lần duyệt nào để huỷ.');
    case 'BAD_BODY': return mk('OTHER', 'yêu cầu không hợp lệ — tải lại trang rồi thử lại.');
    case 'WORKFLOW_RUN_ACTIVE': return mk('BUSY', 'máy tự chạy chặng đang chạy trên plan này — đợi xong rồi bấm Tải lại, đừng bấm lại.');
    case 'JOB_RUNNING': {
      const det = (serverMessageOf(err) ?? '').replace(/^[A-Z][A-Z0-9_]*:\s*/, '').trim();
      // «Truyện đang có runner Tài sản hoặc kết quả cần đối soát» (khoá cấp truyện): nói rõ việc gì chặn + cách gỡ.
      if (/đối soát|runner/i.test(det)) return mk('BUSY', 'truyện đang có lượt chạy tài sản hoặc có kết quả chưa rõ cần đối soát — đợi lượt đang chạy xong; nếu không còn lượt nào chạy mà vẫn báo, đối soát kết quả chưa rõ ở bước Tài sản rồi bấm lại.');
      return mk('BUSY', `plan đang có việc chạy ở nền${det ? ` (${det})` : ''} — đợi xong rồi bấm Tải lại, đừng bấm lại.`);
    }
    default: break;
  }
  if (err?.response?.status === 404 && !code) return mk('OTHER', 'máy chủ chưa hỗ trợ thao tác này — cập nhật máy chủ rồi tải lại.');
  return workflowActionError(err, what);
}

// ───────────────────────── requestId ─────────────────────────

/** UUID v4 cho `requestId`. jsdom / trình duyệt cũ có thể thiếu `crypto.randomUUID` ⇒ rơi về `getRandomValues`, rồi `Math.random`. */
export function newRequestId(src: { randomUUID?: () => string; getRandomValues?: (a: Uint8Array) => Uint8Array } | undefined = (globalThis as any).crypto): string {
  if (src && typeof src.randomUUID === 'function') return src.randomUUID();
  const b = new Uint8Array(16);
  if (src && typeof src.getRandomValues === 'function') src.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// ───────────────────────── cờ lỗi bền (QĐ 9) ─────────────────────────

export const typeLabelOf = (key: string): string => {
  const m = /^(?:missing:)?(look|location|prop|event|map):/.exec(key);
  return m ? ({ look: 'Nhân vật', location: 'Bối cảnh', prop: 'Đạo cụ', event: 'Video', map: 'Sơ đồ' } as const)[m[1] as 'look' | 'location' | 'prop' | 'event' | 'map'] : 'Mục';
};
const FLAG_OUTCOMES: ReadonlySet<string> = new Set(['FAILED', 'BLOCKED', 'UNCERTAIN', 'NOT_RUN_STOP', 'NOT_RUN_BUDGET', 'NOT_RUN_INTERRUPTED', 'SKIPPED_MANUAL']);
/** Hàng cờ của một bước: LỖI (`kind: ERROR`) và GIỮ SỬA TAY (`kind: KEPT`) — phân loại theo envelope thật của BE (`workflowFlagUtils`). */
export function stepFlagRowsOf(s: WorkflowStatus, step: WorkflowStepKey, names?: Record<string, string>): StepFlagRow[] {
  return (s.stepRuns?.[step]?.objects ?? [])
    .filter((o) => FLAG_OUTCOMES.has(o.outcome))
    .map((o) => {
      const f = flagInfoOf({ outcome: o.outcome, code: o.error?.code, message: o.error?.message });
      const name = names?.[o.key] ?? (o.ownerType && o.ownerId ? names?.[`${o.ownerType}:${o.ownerId}`] : undefined);
      return { kind: f.kind, key: o.key, ...(name ? { name } : {}), typeLabel: typeLabelOf(o.key), outcomeLabel: f.label, message: f.hint, eventIds: o.eventIds ?? [], outcome: o.outcome, ...(o.error?.code ? { code: o.error.code } : {}), ...(o.error?.message ? { rawMessage: o.error.message } : {}), ...(o.ownerType ? { ownerType: o.ownerType } : {}) };
    });
}
export const assetFlagRowsOf = (s: WorkflowStatus): StepFlagRow[] => stepFlagRowsOf(s, 'ASSETS');
